'use client';

import React, { useState } from 'react';
import {
    LayoutDashboard,
    Users,
    GraduationCap,
    Info,
    Wallet,
    BarChart3,
    Banknote,
    HandCoins,
    ArrowRightLeft,
    Receipt,
    CreditCard,
    TrendingUp,
    FileText,
    Library,
    PieChart,
    Calculator,
    ShieldCheck,
    Menu,
    ChevronDown,
    ChevronRight,
    LucideIcon,
    FileStack,
    CheckSquare,
    ClipboardList,
    Settings2,
    UsersRound,
    UserCog,
    LogOut,
    ArrowLeft
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavChild {
    name: string;
    href: string;
    icon: LucideIcon;
}

interface NavItem {
    name: string;
    icon: LucideIcon;
    href?: string;
    children?: NavChild[];
}

interface NavGroup {
    group: string;
    items: NavItem[];
}

// Navigation for Kepala Sekolah
const kepalaSekolahNavItems: NavGroup[] = [
    {
        group: 'Main Dashboard',
        items: [
            { name: 'Dashboard Kepsek', icon: LayoutDashboard, href: '/sekolah' },
            { name: 'Dashboard Guru', icon: LayoutDashboard, href: '/sekolah/dashboard-guru' },
        ]
    },
    {
        group: 'Manajemen SDM',
        items: [
            { name: 'Data Guru', icon: Users, href: '/sekolah/guru' },
            { name: 'Data Staff', icon: UsersRound, href: '/sekolah/staff' },
            { name: 'Data Siswa', icon: GraduationCap, href: '/sekolah/siswa' },
        ]
    },
    {
        group: 'Operasional',
        items: [
            { name: 'Approval Dana', icon: CheckSquare, href: '/sekolah/approval' },
            { name: 'Pengajuan Dana', icon: ClipboardList, href: '/sekolah/pengajuan-dana' },
        ]
    },
    {
        group: 'Keuangan & Akuntansi',
        items: [
            { name: 'Data List Akun (COA)', icon: FileStack, href: '/sekolah/akun' },
            { name: 'Rekening Bank', icon: Banknote, href: '/sekolah/rekening' },
            {
                name: 'Setoran',
                icon: HandCoins,
                children: [
                    { name: 'Setoran Modal', href: '/sekolah/setoran-modal', icon: TrendingUp },
                    { name: 'Setoran Masuk', href: '/sekolah/setoran-masuk', icon: HandCoins },
                ]
            },
            { name: 'Transfer Kas Antar Akun', icon: ArrowRightLeft, href: '/sekolah/transfer' },
            { name: 'Pengeluaran Biaya', icon: Receipt, href: '/sekolah/pengeluaran-biaya' },
        ]
    },
    {
        group: 'Laporan Keuangan',
        items: [
            {
                name: 'Laporan',
                icon: BarChart3,
                children: [
                    { name: 'Jurnal', href: '/sekolah/laporan/jurnal', icon: FileText },
                    { name: 'Buku Besar', href: '/sekolah/laporan/buku-besar', icon: Library },
                    { name: 'Laba Rugi', href: '/sekolah/laporan/laba-rugi', icon: PieChart },
                    { name: 'Perubahan Modal', href: '/sekolah/laporan/modal', icon: BarChart3 },
                    { name: 'Neraca', href: '/sekolah/laporan/neraca', icon: Calculator },
                ]
            },
        ]
    },
    {
        group: 'Sistem',
        items: [
            {
                name: 'Master Data',
                icon: Settings2,
                children: [
                    { name: 'Akun Jurnal (Kategori)', href: '/sekolah/master-data/kategori', icon: FileStack },
                    { name: 'Role & Permissions', href: '/sekolah/master-data/role', icon: ShieldCheck },
                    { name: 'Manajemen Akun', href: '/sekolah/master-data/akun', icon: UserCog },
                ]
            },
        ]
    },
];

interface SidebarProps {
    className?: string;
    onClose?: () => void;
    collapsed?: boolean;
    setCollapsed?: (collapsed: boolean) => void;
}

export default function Sidebar({ className = "", onClose, collapsed = false, setCollapsed }: SidebarProps) {
    const pathname = usePathname();
    const [openSubMenus, setOpenSubMenus] = useState<string[]>(['Setoran', 'Laporan', 'Master Data']);

    const getNavItems = () => {
        return kepalaSekolahNavItems;
    };

    const navItems = getNavItems();

    const toggleSubMenu = (name: string) => {
        setOpenSubMenus(prev =>
            prev.includes(name)
                ? prev.filter(item => item !== name)
                : [...prev, name]
        );
    };

    const isSubMenuOpen = (name: string) => openSubMenus.includes(name);

    return (
        <>
            {/* Desktop Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-40 h-screen transition-all duration-500 ease-in-out bg-surface border-r border-border-main hidden lg:flex flex-col shadow-2xl shadow-primary/5 ${collapsed ? 'w-24' : 'w-72'
                    } ${className}`}
            >
                {/* Logo Area */}
                <div className={`p-8 mb-4 transition-all duration-500 flex items-center ${collapsed ? 'justify-center' : 'justify-start'}`}>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30 shrink-0 group hover:rotate-12 transition-transform">
                            <GraduationCap className="text-white w-6 h-6" />
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col animate-in fade-in slide-in-from-left-4 duration-500">
                                <span className="font-black text-foreground tracking-tight text-lg uppercase">Dashboard<span className="text-primary ml-1">Sekolah</span></span>
                                <span className="text-[9px] font-black text-muted uppercase tracking-[0.3em] -mt-0.5">Management Portal</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto px-4 space-y-8 custom-scrollbar pb-10">
                    {navItems.map((group, groupIdx) => (
                        <div key={groupIdx} className="space-y-4">
                            {!collapsed && (
                                <h3 className="px-4 text-[10px] font-black text-muted uppercase tracking-[0.2em] opacity-50">
                                    {group.group}
                                </h3>
                            )}
                            <div className="space-y-1.5">
                                {group.items.map((item, itemIdx) => {
                                    const isActive = item.href ? pathname === item.href : item.children?.some(child => pathname === child.href);
                                    const isOpen = isSubMenuOpen(item.name);

                                    return (
                                        <div key={itemIdx} className="space-y-1">
                                            {item.href ? (
                                                <Link
                                                    href={item.href}
                                                    className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${isActive
                                                        ? 'bg-primary text-white shadow-xl shadow-primary/20 translate-x-1'
                                                        : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                        }`}
                                                >
                                                    <item.icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                                                    {!collapsed && <span className="text-xs font-bold leading-none">{item.name}</span>}
                                                    {isActive && (
                                                        <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                    )}
                                                </Link>
                                            ) : (
                                                <div className="space-y-1">
                                                    <button
                                                        onClick={() => toggleSubMenu(item.name)}
                                                        className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive && !isOpen ? 'bg-primary-soft text-primary' : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                            }`}
                                                    >
                                                        <item.icon className="w-5 h-5" />
                                                        {!collapsed && (
                                                            <>
                                                                <span className="text-xs font-bold grow text-left">{item.name}</span>
                                                                {isOpen ? (
                                                                    <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                                                                ) : (
                                                                    <ChevronRight className="w-4 h-4 transition-transform duration-300" />
                                                                )}
                                                            </>
                                                        )}
                                                    </button>
                                                    {isOpen && !collapsed && (
                                                        <div className="ml-6 space-y-1 border-l-2 border-primary/10 pl-4 animate-in slide-in-from-top-2 duration-300">
                                                            {item.children?.map((child, childIdx) => {
                                                                const isChildActive = pathname === child.href;
                                                                return (
                                                                    <Link
                                                                        key={childIdx}
                                                                        href={child.href}
                                                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${isChildActive
                                                                            ? 'text-primary font-black bg-primary-soft'
                                                                            : 'text-muted hover:text-primary'
                                                                            }`}
                                                                    >
                                                                        <child.icon className="w-3.5 h-3.5" />
                                                                        <span className="text-[11px] font-bold">{child.name}</span>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>


            </aside>
        </>
    );
}
